import React from "react";
import {
  Box,
  Card,
  Divider,
  Skeleton,
  TableContainer,
  TablePagination,
  alpha,
} from "@mui/material";
import { COLORS } from "../../styles/uiConstants";
import EmptyState from "../atoms/EmptyState";
import SearchIllustration from "../atoms/EmptyState/SearchIllustration";

const cardSx = {
  borderRadius: 2,
  overflow: "hidden",
  boxShadow: "0 12px 32px rgba(15,23,42,0.08)",
  border: `1px solid ${COLORS.slate200}`,
  background: COLORS.white,
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
};

// variant="flat": liste sayfalarında beyaz zemine oturan, çerçevesiz/gölgesiz tablo
const flatCardSx = {
  borderRadius: 0,
  boxShadow: "none",
  border: "none",
  borderTop: `1px solid ${COLORS.slate200}`,
};

const paginationSx = {
  position: "sticky",
  bottom: 0,
  background: "white",
  zIndex: 2,
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Boş durumda arkada duran soluk, hareketsiz satır iskeleti: tablo "dolu ama
// boş" görünür, illüstrasyon ve metin bunun üstünde okunur.
const BACKDROP_ROWS = 12;
const BACKDROP_BARS = ["24%", "16%", "28%", "12%"];

function EmptyBackdrop() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        opacity: 0.55,
        maskImage: "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.6) 45%, transparent 95%)",
        WebkitMaskImage: "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.6) 45%, transparent 95%)",
      }}
    >
      {Array.from({ length: BACKDROP_ROWS }).map((_, r) => (
        <Box
          key={r}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            height: 48,
            px: { xs: 2, md: 4 },
            borderBottom: `1px solid ${COLORS.slate100}`,
          }}
        >
          {BACKDROP_BARS.map((w, c) => (
            <Skeleton
              key={c}
              variant="rounded"
              animation={false}
              height={10}
              sx={{ width: w, bgcolor: COLORS.slate200, flexShrink: 0 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Tablo kartı (organism): sabit yükseklikte TableContainer + boş state + sayfalama.
 * @param {number} tableHeight - TableContainer yüksekliği (px)
 * @param {React.ReactNode} children - Table (stickyHeader, size="small") + TableHead + TableBody
 * @param {boolean} [empty] - Boş state gösterilsin mi
 * @param {React.ReactNode} [emptyIcon] - Boş state ikonu
 * @param {string} [emptyTitle] - Boş state başlığı
 * @param {string} [emptyMessage] - Boş state açıklaması
 * @param {React.ReactNode} [emptyAction] - Boş state altındaki aksiyon (ör. "Yeni hasta ekle" bağlantısı)
 * @param {number} [page] - Sayfa (0 tabanlı)
 * @param {number} [rowsPerPage] - Sayfa başına satır
 * @param {number} [totalCount] - Toplam kayıt
 * @param {function} [onPageChange] - (e, newPage) => void
 * @param {function} [onRowsPerPageChange] - (e) => void
 * @param {number[]} [rowsPerPageOptions] - Seçenekler
 * @param {"card"|"flat"} [variant="card"] - "flat": çerçevesiz, gölgesiz (beyaz zemin üstü liste)
 * @param {boolean} [loading] - yüklenirken iskelet satırlarının üstünde soluk, hareketli
 *   arama çizimi (iskelet satırlarını çağıran çizer; opt-in)
 */
export default function DataTableCard({
  tableHeight,
  children,
  empty = false,
  emptyIcon,
  emptyTitle = "Sonuç bulunamadı",
  emptyMessage,
  emptyAction,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  variant = "card",
  loading = false,
}) {
  return (
    <Card sx={variant === "flat" ? { ...cardSx, ...flatCardSx } : cardSx}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            ...(tableHeight
              ? { height: tableHeight, maxHeight: tableHeight }
              : {}),
            overflow: "auto",
            // Uçlara ulaşınca ne sayfaya taşsın ne de macOS/Safari elastik (bounce) efektiyle
            // sticky başlığı aşağı kaydırsın ("contain" bounce'u kapatmaz, "none" kapatır)
            overscrollBehavior: "none",
          }}
        >
          {children}
        </TableContainer>

        {loading && !empty ? (
          // Yükleme katmanı: iskeletin üstünde soluk, hareketli çizim (tıklamayı engellemez)
          <Box
            role="status"
            aria-label="Yükleniyor"
            sx={{
              position: "absolute",
              inset: 0,
              top: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              opacity: 0.55,
            }}
          >
            <SearchIllustration />
          </Box>
        ) : null}

        {empty ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              // Başlık satırının altından başla; illüstrasyon başlığı örtmesin
              top: 44,
              p: 2,
            }}
          >
            <EmptyBackdrop />
            <Box
              sx={{
                position: "relative",
                pointerEvents: "auto",
                maxHeight: "100%",
                overflow: "hidden",
                // Arkadaki iskelet metni bastırmasın diye yumuşak beyaz hale
                background: `radial-gradient(ellipse at center, ${COLORS.white} 45%, ${alpha(COLORS.white, 0)} 75%)`,
                px: 4,
              }}
            >
              <EmptyState
                variant="illustrated"
                illustration="search"
                icon={emptyIcon}
                title={emptyTitle}
                description={emptyMessage}
                action={emptyAction}
              />
            </Box>
          </Box>
        ) : null}
      </Box>

      <Divider />
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage="Sayfa başı"
        sx={paginationSx}
      />
    </Card>
  );
}
